import type { DateTime } from 'luxon';
import { type ChangeEvent, createContext, type FC, type MouseEvent, type ReactElement, useState } from 'react';
import { usePagination } from '../../content/hooks';
import type { Order, VisitStatus, VisitsGeneralReportData, VisitsGeneralSortingFields } from '../../models';

interface IReportsContext {
  loading: boolean;
  setLoading: (val: boolean) => void;
  dataUpdate: boolean;
  toggleUpdate: () => void;
  data: VisitsGeneralReportData[];
  setData: (val: VisitsGeneralReportData[]) => void;
  page: number;
  setPage: (val: number) => void;
  limit: number;
  setLimit: (val: number) => void;
  count: number;
  setCount: (val: number) => void;
  order: Order;
  setOrder: (val: Order) => void;
  orderBy: VisitsGeneralSortingFields;
  setOrderBy: (val: VisitsGeneralSortingFields) => void;
  handlePageChange: (_event: any, newPage: number) => void;
  handleLimitChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleRequestSort: (_event: MouseEvent, property: VisitsGeneralSortingFields) => void;
  fromDate: DateTime | null;
  setFromDate: (val: DateTime | null) => void;
  toDate: DateTime | null;
  setToDate: (val: DateTime | null) => void;
  status: VisitStatus | null;
  setStatus: (val: VisitStatus | null) => void;
}

export const VisitsGeneralContext = createContext<IReportsContext>({} as IReportsContext);

export const VisitsGeneralProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const [dataUpdate, setDataUpdate] = useState(false);
  const toggleUpdate = (): void => setDataUpdate(!dataUpdate);

  const {
    page,
    setPage,
    limit,
    setLimit,
    count,
    setCount,
    order,
    setOrder,
    orderBy,
    setOrderBy,
    handlePageChange,
    handleLimitChange,
    handleRequestSort
  } = usePagination<VisitsGeneralSortingFields>(toggleUpdate, 'date', 'desc');

  const [data, setData] = useState<VisitsGeneralReportData[]>([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState<DateTime | null>(null);
  const [toDate, setToDate] = useState<DateTime | null>(null);
  const [status, setStatus] = useState<VisitStatus | null>(null);

  return (
    <VisitsGeneralContext.Provider
      value={{
        data,
        setData,
        page,
        setPage,
        limit,
        setLimit,
        count,
        setCount,
        order,
        setOrder,
        orderBy,
        setOrderBy,
        handlePageChange,
        handleLimitChange,
        handleRequestSort,
        loading,
        setLoading,
        dataUpdate,
        toggleUpdate,
        fromDate,
        setFromDate,
        toDate,
        setToDate,
        status,
        setStatus
      }}
    >
      {children}
    </VisitsGeneralContext.Provider>
  );
};
